import { Download01, Image01, RefreshCw01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useRef, useState } from 'react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import csv from 'src/assets/img/FileTypeIcons/csv-lg.svg';
import defaultFileType from 'src/assets/img/FileTypeIcons/default-lg.svg';
import doc from 'src/assets/img/FileTypeIcons/doc-lg.svg';
import pdf from 'src/assets/img/FileTypeIcons/pdf-lg.svg';
import txt from 'src/assets/img/FileTypeIcons/txt-lg.svg';
import xls from 'src/assets/img/FileTypeIcons/xls-lg.svg';
import xlsx from 'src/assets/img/FileTypeIcons/xlsx-lg.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { getFormattedDate, getShortName, onError } from 'src/utils/CommonFunctions';

import { getFileExtension } from '../helpers';
import { IEntityChatMember, IEntityChatMessage } from '../interface';

interface IMessageCardProps {
    message: IEntityChatMessage;
    currentUser: any;
    chatMembers: IEntityChatMember[];
    getFormattedDateTime: (date: any, convertTimezone?: boolean) => string;
    messageRefs: React.MutableRefObject<any[]>;
    onResend: (message: IEntityChatMessage) => void;
    mainIndex: number;
    isFromWidget?: boolean;
}

const MessageCard: React.FC<IMessageCardProps> = ({ message, currentUser, chatMembers, getFormattedDateTime, messageRefs, onResend, mainIndex, isFromWidget = false }) => {
    const notCurrentUserRef = useRef<HTMLParagraphElement>(null);
    const [downloadHoveredIndex, setDownloadHoveredIndex] = useState<number | null>(null);

    if (message.special) {
        return (
            <li className={`flex items-center ${mainIndex === 0 ? '' : 'mt-4'}`} key={message.id}>
                <span className="h-[1px] bg-utilityGray200 w-full"></span>
                <p className="text-grayLight600 text-xs text-center px-2 text-nowrap">{getFormattedDate(message.createdAt, 'MMMM Do, YYYY', false, false)}</p>
                <span className="h-[1px] bg-utilityGray200 w-full"></span>
            </li>
        );
    }

    const isCurrentUser = message.userInfo?.id?.toString() === currentUser?.id?.toString();
    const hasAttachments = message.attachments && message.attachments.length > 0;

    const getFileIcon = (fileType: string, filename?: string) => {
        if (fileType === 'image') {
            return <Image01 className="w-4 h-4" />;
        }

        const extension = getFileExtension(filename);

        switch (extension) {
            case 'pdf':
                return (
                    <img
                        src={pdf}
                        alt="PDF"
                        className={`object-contain ${message.attachments && message.attachments.length > 1 ? (isFromWidget ? 'w-[60px] h-[60px]' : 'w-[100px] h-[100px]') : 'w-10 h-10'}`}
                    />
                );
            case 'xls':
                return (
                    <img
                        src={xls}
                        alt="Excel"
                        className={`object-contain ${message.attachments && message.attachments.length > 1 ? (isFromWidget ? 'w-[60px] h-[60px]' : 'w-[100px] h-[100px]') : 'w-10 h-10'}`}
                    />
                );
            case 'xlsx':
                return (
                    <img
                        src={xlsx}
                        alt="Excel"
                        className={`object-contain ${message.attachments && message.attachments.length > 1 ? (isFromWidget ? 'w-[60px] h-[60px]' : 'w-[100px] h-[100px]') : 'w-10 h-10'}`}
                    />
                );
            case 'doc':
            case 'docx':
                return (
                    <img
                        src={doc}
                        alt="Word Document"
                        className={`object-contain ${message.attachments && message.attachments.length > 1 ? (isFromWidget ? 'w-[60px] h-[60px]' : 'w-[100px] h-[100px]') : 'w-10 h-10'}`}
                    />
                );
            case 'csv':
                return (
                    <img
                        src={csv}
                        alt="CSV"
                        className={`object-contain ${message.attachments && message.attachments.length > 1 ? (isFromWidget ? 'w-[60px] h-[60px]' : 'w-[100px] h-[100px]') : 'w-10 h-10'}`}
                    />
                );
            case 'txt':
                return (
                    <img
                        src={txt}
                        alt="Text File"
                        className={`object-contain ${message.attachments && message.attachments.length > 1 ? (isFromWidget ? 'w-[60px] h-[60px]' : 'w-[100px] h-[100px]') : 'w-10 h-10'}`}
                    />
                );
            default:
                return (
                    <img
                        src={defaultFileType}
                        alt="File"
                        className={`object-contain ${message.attachments && message.attachments.length > 1 ? (isFromWidget ? 'w-[60px] h-[60px]' : 'w-[100px] h-[100px]') : 'w-10 h-10'}`}
                    />
                );
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const isImageFile = (attachment: any) =>
        attachment.fileType === 'image' || attachment.orignalName?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || attachment.name?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);

    const handleDownload = async (url: string, filename: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);
            window.open(url, '_blank');
        }
    };

    const imagePath = message?.userInfo?.image ?? null;

    const memberIsOnline = chatMembers?.find((m) => m?.id?.toString() === message?.userInfo?.id?.toString())?.isOnline ?? false;

    return (
        <li
            className={`flex gap-3 ${isCurrentUser ? 'justify-end' : isFromWidget ? 'max-w-[70%]' : 'max-w-[65%]'}`}
            key={message.id}
            ref={(el) => {
                if (el) {
                    (messageRefs.current as any)[message.id] = el;
                }
            }}
            data-message-id={message.id}
        >
            {!isCurrentUser && message?.showSenderAndDate && (
                <div className="flex gap-2">
                    <div className="w-7 h-7 flex-none relative mt-4">
                        {imagePath ? (
                            <img src={imagePath} className="w-7 h-7 rounded-full uppercase border-gray-200 border-[1px] bg-utilityGray100 text-primary" alt="" onError={onError} />
                        ) : (
                            <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center uppercase border border-primary flex-none mr-2 text-xs">
                                {getShortName(`${message.userInfo?.firstName} ${message.userInfo?.lastName}`)}
                            </div>
                        )}
                        <span
                            className={`border border-white rounded-full w-2 h-2 absolute bottom-0 right-0 user-status-${message?.userInfo?.id} ${
                                memberIsOnline ? 'bg-successSecondary' : 'bg-successSecondary'
                            }`}
                        />
                    </div>
                </div>
            )}

            <div className={isCurrentUser ? (isFromWidget ? 'max-w-[70%]' : 'max-w-[64%]') : ''}>
                {message?.showSenderAndDate && (
                    <div className={`flex items-center w-full mb-1.5 mt-4 ${isCurrentUser ? 'justify-end gap-2' : ''}`}>
                        {!isCurrentUser && (
                            <TruncateTextCmp message={`${message.userInfo?.firstName} ${message.userInfo?.lastName}`} refName={notCurrentUserRef}>
                                <p className="text-grayLight600 font-normal text-xs truncate" ref={notCurrentUserRef}>
                                    {message.userInfo?.firstName} {message.userInfo?.lastName},&nbsp;
                                </p>
                            </TruncateTextCmp>
                        )}
                        <p className="text-grayLight600 font-normal text-xs flex-none">{getFormattedDateTime(message.createdAt, true)}</p>
                    </div>
                )}

                <div className={`flex items-center gap-2 w-fit max-w-full ${isCurrentUser ? 'ml-auto' : ''}`}>
                    {isCurrentUser && message?.hasError && (
                        <TooltipCmp message="Resend message">
                            <ButtonCmp onClick={() => onResend(message)} type="button" size="none">
                                <RefreshCw01 className="w-4 h-4 text-fgErrorPrimary" />
                            </ButtonCmp>
                        </TooltipCmp>
                    )}

                    <div
                        /* min-w-0: this bubble is a flex item, whose automatic minimum size is its
                           min-content width. Without this it refuses to shrink below the longest
                           unbreakable run in the message, so a long string pushed the bubble out to
                           the 50% cap while its text wrapped short of the right edge. */
                        className={`min-w-0 rounded-lg ${
                            isCurrentUser
                                ? `rounded-tr-none xl:p-2.5 p-2 font-normal text-xs bg-primary50 text-grayLight900 mb-0.5 overflow-hidden ${message?.hasError ? 'border border-fgErrorPrimary' : ''}`
                                : `rounded-tl-none border border-utilityGray200 p-2.5 text-grayLight900 font-normal text-xs mb-0.5 bg-utilityGray100 ${message?.showSenderAndDate ? '' : 'ml-10'} ${
                                      message?.hasError ? 'border border-fgErrorPrimary' : ''
                                  }`
                        }`}
                    >
                        {message.content && message.content.trim() !== '' && (
                            /* whitespace-pre-wrap keeps the newlines a sender typed with Shift+Enter —
                               without it a multi-line message collapses into one paragraph.
                               overflow-wrap:anywhere (not the .word-break helper's break-word) because
                               only `anywhere` also shrinks the element's min-content width: the bubble
                               is sized by `w-fit`, so with break-word an unbroken 700-character run
                               still measured as one atom and the bubble stretched to its max while the
                               text wrapped far short of the right edge. */
                            <div className={`w-full whitespace-pre-wrap [overflow-wrap:anywhere] ${hasAttachments ? 'mb-2' : ''}`}>{message.content}</div>
                        )}

                        {hasAttachments && (
                            <PhotoProvider>
                                <div className={`flex flex-wrap gap-2 ${message.attachments && message.attachments.length > 3 ? 'max-w-[208px]' : ''}`}>
                                    {message.attachments?.map((attachment: any, index: number) => {
                                        const isImage = isImageFile(attachment);
                                        const imageUrl = attachment._blobUrl || (attachment.mediaUrl && attachment.media ? `${attachment.mediaUrl}/${attachment.media}` : null);
                                        const fileUrl = attachment.mediaUrl && attachment.media ? `${attachment.mediaUrl}/${attachment.media}` : null;
                                        const isUploading = message.isOptimistic && !message.hasError && !attachment.mediaUrl;

                                        const handleFileClick = () => {
                                            if (fileUrl && !isUploading) {
                                                window.open(fileUrl, '_blank');
                                            }
                                        };

                                        return (
                                            <div key={index} className="max-w-full">
                                                {isImage && imageUrl ? (
                                                    <div className={`relative group ${isFromWidget ? 'w-[60px] h-[60px]' : 'w-[100px] h-[100px]'} flex items-center justify-center`}>
                                                        <PhotoView src={imageUrl}>
                                                            <img
                                                                src={imageUrl}
                                                                alt={attachment.orignalName || attachment.name}
                                                                className="w-full h-full rounded-lg border border-utilityGray200 cursor-pointer hover:opacity-90 transition-opacity object-cover"
                                                                onError={onError}
                                                            />
                                                        </PhotoView>
                                                        {isUploading ? (
                                                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                                                                <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                                                            </div>
                                                        ) : (
                                                            imageUrl &&
                                                            attachment.mediaUrl && (
                                                                <div className="absolute top-0 right-0">
                                                                    <TooltipCmp message="Download file">
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDownload(imageUrl, attachment.orignalName || attachment.name);
                                                                            }}
                                                                            className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center w-[22px] h-[22px] rounded-full bg-white shadow-lgc text-textSecondary hover:text-primary"
                                                                        >
                                                                            <Download01 className="w-[13px] h-[13px]" />
                                                                        </button>
                                                                    </TooltipCmp>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                ) : (
                                                    <TooltipCmp
                                                        message={
                                                            downloadHoveredIndex === index
                                                                ? 'Download file'
                                                                : message?.attachments && message.attachments.length > 1
                                                                ? attachment.orignalName || attachment.name
                                                                : null
                                                        }
                                                    >
                                                        <div
                                                            className={`flex items-center gap-3 relative group ${
                                                                isUploading ? 'cursor-wait opacity-75' : 'cursor-pointer hover:opacity-90 transition-opacity'
                                                            }`}
                                                            onClick={handleFileClick}
                                                        >
                                                            <div>{getFileIcon(attachment.fileType, attachment.orignalName || attachment.name)}</div>
                                                            {message?.attachments?.length === 1 && (
                                                                <div className="flex-1 overflow-hidden">
                                                                    <p className="text-xs leading-[1.5] font-normal text-grayLight900 truncate">{attachment.orignalName || attachment.name}</p>
                                                                    {attachment.fileSize && (
                                                                        <p className="text-xs leading-[1.5] text-gray500 font-normal">{formatFileSize(Number(attachment.fileSize))}</p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {isUploading ? (
                                                                <div className="absolute top-1 right-2">
                                                                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-md">
                                                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                attachment.mediaUrl &&
                                                                attachment.media && (
                                                                    <div className="absolute -top-1 -right-1">
                                                                        <button
                                                                            type="button"
                                                                            onMouseEnter={() => setDownloadHoveredIndex(index)}
                                                                            onMouseLeave={() => setDownloadHoveredIndex(null)}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDownload(`${attachment.mediaUrl}/${attachment.media}`, attachment.orignalName || attachment.name);
                                                                            }}
                                                                            className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center w-[22px] h-[22px] rounded-full bg-white shadow-lgc text-textSecondary hover:text-primary"
                                                                        >
                                                                            <Download01 className="w-[13px] h-[13px]" />
                                                                        </button>
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    </TooltipCmp>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </PhotoProvider>
                        )}
                    </div>
                </div>
            </div>
        </li>
    );
};

export default MessageCard;
